import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePaymentTracking } from "@/hooks/usePaymentTracking";
import { DollarSign, Calendar, CheckCircle, AlertCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export function PaymentTrackingDashboard({ workspaceId }: { workspaceId: string }) {
  const { data: payments, isLoading } = usePaymentTracking(workspaceId);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "pending":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      case "overdue":
        return "bg-red-500/10 text-red-500 border-red-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="h-4 w-4" />;
      case "overdue":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return <div>Loading payment tracking...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Payment Tracking</h3>
        <Badge variant="outline">{payments?.length || 0} payments</Badge>
      </div>

      <div className="space-y-3">
        {payments?.map((payment) => (
          <Card key={payment.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    <p className="font-medium">Bill ID: {payment.bill_id}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge className={getStatusColor(payment.payment_status)}>
                      {getStatusIcon(payment.payment_status)}
                      <span className="ml-1">{payment.payment_status}</span>
                    </Badge>
                    {payment.payment_date && (
                      <Badge variant="outline">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDistanceToNow(new Date(payment.payment_date), { addSuffix: true })}
                      </Badge>
                    )}
                  </div>

                  {payment.payment_method && (
                    <p className="text-sm text-muted-foreground">
                      Method: {payment.payment_method}
                    </p>
                  )}
                </div>

                <Button size="sm" variant="outline">
                  View Details
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!payments || payments.length === 0) && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No payment tracking records
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
